<script setup lang="ts">
import BaseIcon from "@/components/base/BaseIcon.vue";
import { computed, ref, watch } from "vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseSwitcher from "@/components/base/BaseSwitcher.vue";

type sizeFigureStruct = "small" | "medium" | "large" | "extra-large";
type colorFigureStruct =
  | "customer"
  | "white"
  | "white-opacity-15"
  | "white-opacity-40"
  | "purple"
  | "grey-full"
  | "grey-secondary"
  | "primary"
  | "secondary";
type positionFigureStruct = "left" | "right" | "bottom" | "top";

const tabsSizeIcon = ref(["16px", "20px", "24px", "32px", "38px"]);
const tabsRadiusFigure = ref(["1px", "16px", "50%"]);
const tabsSizeFigure = ref<sizeFigureStruct[]>([
  "small",
  "medium",
  "large",
  "extra-large",
]);
const tabsColorFigure = ref<colorFigureStruct[]>([
  "customer",
  "white",
  "white-opacity-15",
  "white-opacity-40",
  "purple",
  "grey-full",
  "grey-secondary",
  "primary",
  "secondary",
]);
const tabsColor = ref([
  "--color-primary",
  "--color-secondary",
  "--color-tertiary",
  "--color-text-primary",
  "--color-text-secondary",
  "--color-text-tertiary",
  "--color-text-addons",
  "--color-text-input",
  "--color-white",
  "--color-grey-light",
  "--color-grey-blue",
  "--color-bg-primary",
  " --color-bg-secondary",
  "--color-bg-tertiary",
  "--color-bg-input",
  "--color-danger-primary",
  "--color-danger-secondary",
  "--color-success",
  "--color-count",
  "--gradient-main",
]);
const tabsPosition = ref<positionFigureStruct[]>([
  "left",
  "right",
  "bottom",
  "top",
]);

const selectSizeIcon = ref("24px");
const selectRadiusFigure = ref("50%");
const selectSizeFigure = ref<sizeFigureStruct>("medium");
const selectColorFigure = ref<colorFigureStruct>("white");
const selectPosition = ref<positionFigureStruct>("right");
const selectColor = ref("--color-secondary");
const inputSize = ref("");
const inputRadiusFigure = ref("");
const textError = ref("");
const switchDisabled = ref(false);
const switchIsButton = ref(false);

const isGradient = computed(() =>
  ["--gradient-main"].includes(selectColor.value)
);

const iconsData = ref({
  folders: [
    {
      name: "ui",
      list: [
        "account",
        "arrow",
        "bell",
        "book",
        "card-pay",
        "check-checkbox",
        "check-sharp",
        "check-success",
        "close",
        "convert",
        "copy",
        "details",
        "dots",
        "download",
        "earth",
        "edit",
        "folder",
        "info",
        "list-search",
        "mail",
        "more",
        "notification",
        "phone",
        "phoneretro",
        "plus",
        "promo",
        "question",
        "rate",
        "ruble",
        "search",
        "service-arrow",
        "services",
        "settings",
        "sim",
        "star",
        "transactions",
        "travel",
      ],
    },
    {
      name: "display",
      list: ["check-success"],
    },
    {
      name: "service",
      list: [
        "ok",
        "rutube",
        "tg",
        "viber",
        "vk",
        "whatsapp",
        "max",
        "yandex-maps",
        "yandex-music",
      ],
    },
    {
      name: "payments",
      list: ["sber-pay"],
    },
  ],
});

const computedColor = computed(() => `var(${selectColor.value})`);

const toggleTabsSizeIcon = (item: string) => {
  selectSizeIcon.value = item;
  inputSize.value = "";
};
const toggleTabsSizeFigure = (item: sizeFigureStruct) => {
  selectSizeFigure.value = item;
};
const toggleTabsColorFigure = (item: colorFigureStruct) => {
  selectColorFigure.value = item;
};
const toggleTabsColor = (item: string) => {
  selectColor.value = item;
};
const toggleTabsPosition = (item: positionFigureStruct) => {
  selectPosition.value = item;
};
const toggleTabsRadiusFigure = (item: string) => {
  selectRadiusFigure.value = item;
  inputRadiusFigure.value = "";
};

watch(
  () => inputSize.value,
  (inputValue) => {
    if (!inputValue) {
      textError.value = "";
      selectSizeIcon.value = "24px";
    } else if (+inputValue <= 3 || +inputValue >= 151)
      return (textError.value =
        "Установлено количественное ограничение на ввод чисел 3<i<151");
    else {
      selectSizeIcon.value = inputValue + "px";
      textError.value = "";
    }
  }
);
watch(
  () => inputRadiusFigure.value,
  (inputValue) => {
    if (!inputValue) {
      selectRadiusFigure.value = "50%";
    } else {
      selectRadiusFigure.value = inputValue;
    }
  }
);
</script>

<template>
  <div class="ui-kit-icon --icon">
    <div class="ui-kit-icon__wrapper">
      <div class="ui-kit-icon__head">
        <span>Icons and Colors </span>
      </div>
      <div class="ui-kit-icon__text">
        <span>BaseIcon</span>
      </div>
      <div class="ui-kit-icon__error footnote color-error">
        <span>{{ textError }} </span>
      </div>
      <div class="ui-kit-icon__selections">
        <div class="ui-kit-icon__selections-field --size">
          <p class="ui-kit-icon__selections-field__name">
            --icon-size (scss, default: 24px):
          </p>
          <div class="ui-kit-icon__selections-field__tabs">
            <div
              v-for="(item, index) in tabsSizeIcon"
              :key="index"
              class="ui-kit-icon__selections-field__tabs-item"
              :class="{ '--active': selectSizeIcon == item }"
              @click="toggleTabsSizeIcon(item)"
            >
              <p class="caption-navbar">
                {{ item }}
              </p>
            </div>
            <div class="ui-kit-icon__selections-field__customer">
              <input
                class="caption-navbar"
                oninput="this.value = this.value.replace(/[^0-9]/g, '')"
                v-model="inputSize"
                type="number"
              />
              <span>px</span>
            </div>
          </div>
        </div>
        <div class="ui-kit-icon__selections-field --color">
          <p class="ui-kit-icon__selections-field__name">color (scss):</p>
          <div class="ui-kit-icon__selections-field__tabs">
            <div
              v-for="(item, index) in tabsColor"
              :key="index"
              class="ui-kit-icon__selections-field__tabs-item"
              :class="{ '--active': selectColor == item }"
              @click="toggleTabsColor(item)"
            >
              <p class="caption-navbar">
                {{ item }}
              </p>
            </div>
          </div>
          если вдруг придётся применить к базовой иконке градиент, то передать
          пропсом в baseIcon isGradient и в стилях задать variables
          --icon-gradient-background
        </div>
        <div class="ui-kit-icon__selections-field --position">
          <p class="ui-kit-icon__selections-field__name">
            position (props) (актуально для стрелок, default: "right"):
          </p>
          <div class="ui-kit-icon__selections-field__tabs">
            <div
              v-for="(item, index) in tabsPosition"
              :key="index"
              class="ui-kit-icon__selections-field__tabs-item"
              :class="{ '--active': selectPosition == item }"
              @click="toggleTabsPosition(item)"
            >
              <p class="caption-navbar">
                {{ item }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="icons-folder-item"
        v-for="(folder, indexFolder) in iconsData.folders"
        :key="indexFolder"
      >
        <p class="icons-folder-item__name caption-normal">
          folder: {{ folder.name }}
        </p>
        <div class="icons-folder-item__descr">
          <span class="footnote" v-if="folder.name === 'ui'"
            >Папка "ui" - по умолчанию: квадратные иконки
          </span>
          <span class="footnote" v-if="folder.name === 'display'"
            >В папке "display": неквадратные иконки, окно просмотра ровно по
            svg. Размеры меняются переопределением width/height через класс в
            месте вызова компонента.
          </span>
          <span class="footnote" v-if="folder.name === 'service'">
            При сохранении оригинальных цветов, передавать props
            is-original-color
          </span>
        </div>
        <div class="ui-kit-icon__row --line --wrap">
          <div
            v-for="(icon, index) in folder.list"
            :key="index"
            class="icon-block --folder"
          >
            <p class="icon-block__name">
              {{ icon }}
            </p>
            <div
              class="icon-block__component"
              :class="[{ '--gradient': isGradient }, `--folder-${folder.name}`]"
            >
              <BaseIcon
                :name="icon as string"
                :folder="folder.name"
                :is-original-color="
                  ['service', 'payments'].includes(folder.name)
                "
                :position="selectPosition"
                :isGradient="isGradient"
                class="icon"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="ui-kit-icon__container ui-kit-icon__row --wrap">
        <div class="ui-kit-icon__text">
          <span>BaseFigureForIcon </span>
        </div>
        <div class="ui-kit-icon__selections">
          <div class="ui-kit-icon__selections-field --size">
            <p class="ui-kit-icon__selections-field__name">
              size (props, default: medium):
            </p>
            <div class="ui-kit-icon__selections-field__tabs">
              <div
                v-for="(item, index) in tabsSizeFigure"
                :key="index"
                class="ui-kit-icon__selections-field__tabs-item"
                :class="{ '--active': selectSizeFigure == item }"
                @click="toggleTabsSizeFigure(item)"
              >
                <p class="caption-navbar">
                  {{ item }}
                </p>
              </div>
            </div>
          </div>
          <p>
            ( 24*24 - small, 36*36 - medium, 48*48 - large, 64*64 - extra-large
            )
          </p>
          <div class="ui-kit-icon__selections-field --color">
            <p class="ui-kit-icon__selections-field__name">
              color (props, default: "white"):
            </p>
            <div class="ui-kit-icon__selections-field__tabs">
              <div
                v-for="(item, index) in tabsColorFigure"
                :key="index"
                class="ui-kit-icon__selections-field__tabs-item"
                :class="{ '--active': selectColorFigure == item }"
                @click="toggleTabsColorFigure(item)"
              >
                <p class="caption-navbar">
                  {{ item }}
                </p>
              </div>
            </div>
          </div>
          <div class="ui-kit-icon__selections-field --radius">
            <p class="ui-kit-icon__selections-field__name">
              radius (props, default: "50%"):
            </p>
            <div class="ui-kit-icon__selections-field__tabs">
              <div
                v-for="(item, index) in tabsRadiusFigure"
                :key="index"
                class="ui-kit-icon__selections-field__tabs-item"
                :class="{ '--active': selectRadiusFigure == item }"
                @click="toggleTabsRadiusFigure(item)"
              >
                <p class="caption-navbar">
                  {{ item }}
                </p>
              </div>
              <div class="ui-kit-icon__selections-field__customer">
                <input class="caption-navbar" v-model="inputRadiusFigure" />
              </div>
            </div>
          </div>

          <div class="ui-kit-icon__selections-field --position">
            <p class="ui-kit-icon__selections-field__name">
              position (props) (актуально для стрелок, default: "right"):
            </p>
            <div class="ui-kit-icon__selections-field__tabs">
              <div
                v-for="(item, index) in tabsPosition"
                :key="index"
                class="ui-kit-icon__selections-field__tabs-item"
                :class="{ '--active': selectPosition == item }"
                @click="toggleTabsPosition(item)"
              >
                <p class="caption-navbar">
                  {{ item }}
                </p>
              </div>
            </div>
          </div>
          <div class="ui-kit-icon__selections-field --isButton">
            <p class="ui-kit-icon__selections-field__name">
              is-button (props, default: false):
            </p>
            <div class="ui-kit-icon__selections-field__switch">
              <BaseSwitcher v-model="switchIsButton" />
            </div>
          </div>
          <div class="ui-kit-icon__selections-field --disabled">
            <p class="ui-kit-icon__selections-field__name">
              disabled (props, default: false):
            </p>
            <div class="ui-kit-icon__selections-field__switch">
              <BaseSwitcher v-model="switchDisabled" />
            </div>
          </div>
        </div>

        <div
          class="ui-kit-icon__row --line --wrap"
          :class="{
            '--grey-bg ': [
              'white',
              'customer',
              'white-opacity-15',
              'white-opacity-40',
              'purple',
            ].includes(selectColorFigure),
          }"
        >
          <div
            v-for="(icon, index) in iconsData.folders[0].list"
            :key="index"
            class="icon-block"
          >
            <p class="icon-block__name">
              {{ icon }}
            </p>
            <div class="icon-block__component">
              <BaseFigureForIcon
                :icon="icon"
                :color="selectColorFigure"
                :size="selectSizeFigure"
                :position="selectPosition"
                :radius="selectRadiusFigure"
                :disabled="switchDisabled"
                :is-button="switchIsButton"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/style/main";

.customer-color-success {
  background-color: #007e00;
  color: var(--color-white);
}
.ui-kit-icon {
  width: 100%;
  height: 100%;
  padding-top: 30px;

  &__wrapper {
    padding: 20px 41px;
    background-color: var(--color-white);
    border-radius: 20px;
  }
  &__selections {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-direction: column;

    &-field {
      display: flex;
      flex-direction: row;
      gap: 5px;
      align-items: start;
      &__name {
        margin-top: 6px;
      }
      @media (max-width: 768px) {
        flex-direction: column;
        align-items: start;
      }
      &.--color {
        align-items: start;
      }
      &__tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        &-item {
          cursor: pointer;
          padding: 5px;
          border-radius: 5px;
          transition: all 0.5s;
          &.--active {
            color: v-bind('isGradient ? "var(--color-white)": computedColor');
            background: v-bind(
              'isGradient ? computedColor: "var(--color-grey-blue)"'
            );
          }
        }
      }
      &__customer {
        height: 30px;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
        input {
          background-color: var(--color-grey-blue);
          border-radius: 5px;
          width: 80px;
          height: 100%;
        }
      }
    }
  }
  &__grid {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 20px;
    display: grid;
  }
  &__container {
  }
  &__head {
    padding: 20px 0 10px;
    font-size: 20px;
    font-weight: bold;
    transition: all 0.4s;
    color: v-bind(computedColor);
  }
  &__text {
    padding: 20px 0 10px;
    font-size: 16px;
    font-weight: bold;
    transition: all 0.4s;
    color: v-bind(computedColor);
  }
  &__error {
    height: 15px;
  }
  &__row {
    display: flex;
    flex-direction: column;
    gap: 15px;
    transition: all 0.4s;
    &.--grey-bg {
      background-color: var(--color-bg-primary);
      border-radius: 20px;
      padding: 15px;
    }
    &.--line {
      flex-direction: row;
      flex-wrap: nowrap;
      &.--wrap {
        flex-wrap: wrap;
      }
    }
  }

  .icons-folder-item {
    margin-top: 20px;
    &__name {
      margin-bottom: 10px;
    }
    &__descr {
      margin-bottom: 20px;
    }
  }
  .icon-block {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: center;
    width: 70px;
    &.--folder {
      width: 140px;
    }

    &__name {
      font-size: 10px;
      min-height: 20px;
    }
    &__component {
      /*родителю или самому компоненту, например: --icon-size: 20px;*/
      --icon-size: #{v-bind(selectSizeIcon)};
      color: v-bind(computedColor);
      /*если вдруг придётся применить к базовой иконке градиент, то передать пропсом в baseIcon isGradient и  в стилях задать variables --icon-gradient-background */
      &.--gradient {
        --icon-gradient-background: v-bind(computedColor);
      }
      &.--folder-display {
        /*В папке "display": неквадратные иконки, окно просмотра ровно по svg. Размеры меняются переопределением width/height через класс в месте вызова компонента*/
        .icon {
          height: 120px;
          width: 156px;
        }
      }
    }
  }
}
</style>
